import React from "react";
import { Link } from "react-router-dom";
import logo from "../icons/logo.svg";
import { FaXTwitter, FaInstagram, FaDiscord } from "react-icons/fa6";
import { SiGumroad } from "react-icons/si";
import { PiThreadsLogoFill } from "react-icons/pi";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const mainLinks = [
    { name: "Case Studies", path: "/case-studies" },
    { name: "Marketplace", path: "/marketplace" },
    { name: "Community", path: "/community" },
    { name: "Members", path: "/members" },
    { name: "Pricing", path: "/pricing" },
    { name: "About", path: "/about" },
    { name: "Tools", path: "/tools" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  const toolsLinks = {
    developer: [
      { name: "SVG to CSS", path: "/tools/svg-to-css" },
      { name: "Base64 Tools", path: "/tools/base64" },
      { name: "Markdown Editor", path: "/tools/markdown-editor" },
      { name: "Image Optimizer", path: "/tools/image-optimizer" },
      { name: "Code Minifier", path: "/tools/code-minifier" },
      { name: "SVG Optimizer", path: "/tools/svg-optimizer" },
      { name: "Lorem Generator", path: "/tools/lorem-generator" },
      { name: "Favicon Generator", path: "/tools/favicon-generator" },
    ],
    design: [
      { name: "Contrast Checker", path: "/tools/contrast-checker" },
      { name: "Shadow Generator", path: "/tools/css-shadow-generator" },
      { name: "Color from Image", path: "/tools/color-from-image" },
      { name: "Clip Path Generator", path: "/tools/css-clip-path-generator" },
    ],
    conversion: [
      { name: "Unit Converter", path: "/tools/unit-converter" },
      { name: "Image Converter", path: "/tools/image-converter" },
      { name: "Aspect Ratio Calc", path: "/tools/aspect-ratio-calculator" },
      { name: "Converters", path: "/converters" },
    ],
  };

  const socialLinks = [
    {
      name: "Instagram",
      icon: FaInstagram,
      url: "https://instagram.com/modular_web",
    },
    {
      name: "Threads",
      icon: PiThreadsLogoFill,
      url: "https://threads.net/modular_web",
    },
    {
      name: "Gumroad",
      icon: SiGumroad,
      url: "https://modularweb.gumroad.com/",
    },
    {
      name: "Twitter",
      icon: FaXTwitter,
      url: "https://x.com/modular_web",
    },
    {
      name: "Discord",
      icon: FaDiscord,
      url: "https://discord.gg/4HyuPeCpEA",
    }
  ];

  return (
    <footer className="w-full bg-black border-t border-white/10">
      <div className="w-full px-5 py-24 md:py-32">
        <div className="max-w-screen-xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16 mb-16">
            {/* Left Column - Logo and Description */}
            <div className="md:col-span-4">
              <Link to="/" className="block mb-8">
                <img src={logo} alt="Modular Logo" className="h-8" />
              </Link>
              <p className="text-neutral-400 leading-relaxed mb-8 max-w-md">
                We build enterprise-grade digital solutions that drive business growth. From innovative startups to established enterprises, we deliver measurable results.
              </p>
              <div className="flex gap-4">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="size-12 flex items-center justify-center rounded-xl bg-white/5 text-neutral-400 hover:bg-blue-500/10 hover:text-blue-400 transition-all duration-200"
                    aria-label={social.name}
                  >
                    <social.icon className="size-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="md:col-span-2">
              <h3 className="text-white text-lg font-medium mb-4">Navigation</h3>
              <ul className="space-y-4">
                {mainLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      className="text-neutral-400 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/get-quote"
                    className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                  >
                    Get Free Quote
                  </Link>
                </li>
              </ul>
            </div>

            {/* Tools Sections */}
            <div className="md:col-span-6 grid grid-cols-1 md:grid-cols-3 gap-8">
             
              {/* Developer Tools */}
              <div>
                <h3 className="text-white text-lg font-medium mb-4">Dev Tools</h3>
                <ul className="space-y-4">
                  {toolsLinks.developer.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        className="text-neutral-400 hover:text-white transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

               {/* Design Tools */}
               <div>
                <h3 className="text-white text-lg font-medium mb-4">Design Tools</h3>
                <ul className="space-y-4">
                  {toolsLinks.design.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        className="text-neutral-400 hover:text-white transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Conversion Tools */}
              <div>
                <h3 className="text-white text-lg font-medium mb-4">Converters</h3>
                <ul className="space-y-4">
                  {toolsLinks.conversion.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        className="text-neutral-400 hover:text-white transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-white/10">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-neutral-400">
                © {currentYear} Modular. All rights reserved.
              </p>
              <div className="flex items-center gap-8">
                <a 
                  href="mailto:hello@modular-web.com" 
                  className="text-sm text-neutral-400 hover:text-white transition-colors"
                >
                  hello@modular-web.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
